import { useLayoutEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import type { TFunction } from 'i18next';
import Linkify from 'linkify-react';
import type { IntermediateRepresentation } from 'linkifyjs';
import { translateMapObjectMessageMapObjectsMapObjectIdTranslateMessagePost } from '../../client';
import type { MapObject } from '../../models/mapObject';
import { showAlert } from '../../utils/telegram/alert';
import { SYSTEM_FONT_STABLE } from '../../utils/typography';
import { cn } from '../cn';
import type { OwnerMessageTranslationState } from './types';


export interface OwnerMessageSectionProps {
  plot: MapObject;
  ownerMessage: string;
  isOwner: string | boolean | null | undefined;
  handleEditMessageClick: () => void;
  translation: OwnerMessageTranslationState;
  setTranslation: Dispatch<SetStateAction<OwnerMessageTranslationState>>;
  t: TFunction;
  i18n: { language: string };
}

export default function OwnerMessageSection({
  plot,
  ownerMessage,
  isOwner,
  handleEditMessageClick,
  translation,
  setTranslation,
  t,
  i18n,
}: OwnerMessageSectionProps) {
  const isTranslating = translation.status === 'loading';
  const showingTranslation = translation.status === 'ready' && translation.showing;
  // Match prior truthiness: empty translated text falls back to original
  const displayMessage =
    translation.status === 'ready' && translation.showing && translation.text
      ? translation.text
      : ownerMessage;

  // A long message used to push "Enter house" below the fold, so guests
  // visited less. Collapse to 3 lines; offer the toggle only when the text
  // actually overflows (measured, since blank lines and emoji vary).
  const textRef = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el || expanded) return;
    setOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [displayMessage, expanded]);

  return (
    <div className="box-border mb-xl w-full rounded-[5px] border border-deep-blue p-lg">
      <p className="m-0 mb-sm [font-family:Lato,sans-serif] text-[14px] leading-[16px] font-light text-white italic">
        {t("Owner's message for guests")}
      </p>
      <div className="flex w-full items-start justify-end gap-lg">
        <p
          ref={textRef}
          className={cn(
            'm-0 flex-1 text-[14px] leading-[20px] font-normal tracking-[0px] whitespace-pre-wrap text-white',
            !expanded && 'line-clamp-3',
          )}
          style={{
            fontFamily: SYSTEM_FONT_STABLE,
          }}
        >
          <Linkify
            options={{
              formatHref: { mention: (href: string) => 'https://t.me' + href },
              render: ({ attributes, content }: IntermediateRepresentation) => {
                const { href, ...rest } = attributes;
                return (
                  <a
                    {...rest}
                    href={href}
                    className="text-brand underline"
                    onClick={(e) => {
                      e.preventDefault();
                      window.open(href, '_blank', 'noopener,noreferrer');
                    }}
                  >
                    {content}
                  </a>
                );
              },
            }}
          >
            {displayMessage}
          </Linkify>
        </p>
        {isOwner && (
          <button
            onClick={handleEditMessageClick}
            className="m-0 flex h-4xl w-4xl shrink-0 cursor-pointer items-center justify-center border-none bg-transparent p-0"
            aria-label={t("Edit message")}
          >
            <img
              src="/ui/world-map/pen-change-owner-message.svg"
              alt=""
              className="block h-4xl w-4xl"
            />
          </button>
        )}
      </div>
      {(overflows || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-xs block cursor-pointer border-none bg-transparent p-0 [font-family:Lato,sans-serif] text-[13px] font-semibold text-brand"
        >
          {expanded ? t("Show less") : t("Show more")}
        </button>
      )}
      {ownerMessage && (
        <button
          onClick={async () => {
            // Prior: if (translatedMessage) toggle showing; empty string re-fetched
            if (translation.status === 'ready' && translation.text) {
              setTranslation({
                status: 'ready',
                text: translation.text,
                showing: !translation.showing,
              });
              return;
            }
            setTranslation({ status: 'loading' });
            try {
              // Language is automatically detected from headers (X-User-Manually-Selected-Language or Accept-Language)
              const { data } = await translateMapObjectMessageMapObjectsMapObjectIdTranslateMessagePost({
                path: { map_object_id: plot.id },
                throwOnError: true,
              });
              setTranslation({
                status: 'ready',
                text: data.translated_text,
                showing: true,
              });
            } catch {
              showAlert(t("Translation failed"));
              setTranslation({ status: 'idle' });
            }
          }}
          disabled={isTranslating}
          className={cn(
            'mt-md self-start rounded-md border border-brand-alpha-50 bg-transparent px-xl py-sm [font-family:Lato,sans-serif] text-[13px] font-semibold text-brand',
            isTranslating ? 'cursor-default opacity-60' : 'cursor-pointer opacity-100',
          )}
        >
          {isTranslating ? '...' : showingTranslation ? t("Show original") : `${t("Translate")} (${i18n.language.split('-')[0].toUpperCase()})`}
        </button>
      )}
    </div>
  );
}
